import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getSocietyAdminData,
  getEnrichedPendingBillPayments,
  approveBillPaymentsBatch,
  rejectBillPaymentsBatch,
  resolveDocumentImageUrl,
  revokeBlobUrl,
  PendingPaymentApprovalGroup,
} from '../services/api';
import { HiRefresh, HiCheckCircle, HiXCircle } from '../components/Icons';
import { formatBillingDateTime } from '../utils/billingDateUtils';
import './SocietyAdminBillPayments.css';

const formatMoney = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);

const SocietyAdminBillPayments: React.FC = () => {
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [groups, setGroups] = useState<PendingPaymentApprovalGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [screenshotUrls, setScreenshotUrls] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<PendingPaymentApprovalGroup | null>(null);
  const [waiveByCategory, setWaiveByCategory] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!societyId) {
      setError('Society not configured for this account');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await getEnrichedPendingBillPayments(societyId);
      setGroups(res.data);
      const urls: Record<string, string> = {};
      for (const g of res.data) {
        if (g.screenshot_file_path) {
          try {
            urls[g.group_key] = await resolveDocumentImageUrl(g.screenshot_file_path);
          } catch {
            /* ignore */
          }
        }
      }
      setScreenshotUrls(urls);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      Object.values(screenshotUrls).forEach((url) => revokeBlobUrl(url));
    };
  }, [screenshotUrls]);

  const openReview = (g: PendingPaymentApprovalGroup) => {
    setSelected(g);
    const initial: Record<string, string> = {};
    for (const slice of g.payment_by_category) {
      initial[slice.bill_type] = waiveByCategory[slice.bill_type] ?? '';
    }
    setWaiveByCategory((prev) => ({ ...prev, ...initial }));
  };

  const closeModal = () => {
    setSelected(null);
  };

  const buildWaivePayload = (g: PendingPaymentApprovalGroup): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const slice of g.payment_by_category) {
      const raw = waiveByCategory[slice.bill_type] ?? '0';
      const val = Math.max(0, Number(raw) || 0);
      if (val > slice.interest_waivable_max + 0.01) {
        throw new Error(
          `Interest waiver for ${slice.category_display_name} cannot exceed ${formatMoney(slice.interest_waivable_max)}`,
        );
      }
      if (val > 0) out[slice.bill_type] = val;
    }
    return out;
  };

  const handleApprove = async (g: PendingPaymentApprovalGroup) => {
    setBusyKey(g.group_key);
    setError('');
    try {
      const waived = buildWaivePayload(g);
      await approveBillPaymentsBatch(g.payment_ids, waived);
      setGroups((prev) => prev.filter((x) => x.group_key !== g.group_key));
      closeModal();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setBusyKey(null);
    }
  };

  const handleReject = async (g: PendingPaymentApprovalGroup) => {
    if (!window.confirm('Reject this payment? The resident will need to submit again.')) return;
    setBusyKey(g.group_key);
    setError('');
    try {
      await rejectBillPaymentsBatch(g.payment_ids);
      setGroups((prev) => prev.filter((x) => x.group_key !== g.group_key));
      closeModal();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Reject failed');
    } finally {
      setBusyKey(null);
    }
  };

  const flatDisplay = (g: PendingPaymentApprovalGroup) =>
    g.flat_number
      ? `${g.block_name ? `${g.block_name} · ` : ''}Flat ${g.flat_number}`
      : g.flat_label;

  return (
    <div className="bp-page">
      <div className="bp-top-bar">
        <div>
          <h2>Pending payment requests</h2>
          <p className="bp-muted">
            Match each request to a bank statement line in{' '}
            <Link to="/society/reconcile">Reconciliation</Link>, then approve to update the flat ledger.
          </p>
        </div>
        <button type="button" className="bp-btn secondary" onClick={load} disabled={loading}>
          <HiRefresh size={18} /> Refresh
        </button>
      </div>

      {error && <div className="bp-error">{error}</div>}

      {loading ? (
        <p className="bp-muted">Loading…</p>
      ) : groups.length === 0 ? (
        <div className="bp-empty-card">
          <p>No payments waiting for approval.</p>
        </div>
      ) : (
        <div className="bp-list">
          {groups.map((g) => (
            <article key={g.group_key} className="bp-card bp-card-compact">
              <div className="bp-card-header">
                <div>
                  <h3>{flatDisplay(g)}</h3>
                  <p className="bp-muted">
                    Paid {formatMoney(g.total_amount_paid)}
                    {g.transaction_id ? ` · Ref ${g.transaction_id}` : ''}
                  </p>
                  <p className="bp-muted">
                    {formatBillingDateTime(g.paid_at)}
                    {g.payment_mode ? ` · ${g.payment_mode}` : ''}
                    {g.payment_ids.length > 1 ? ` · ${g.payment_ids.length} categories` : ''}
                  </p>
                  <p className={`bp-bank-match ${g.bank_matched ? 'matched' : 'unmatched'}`}>
                    {g.bank_matched
                      ? `Matched to bank statement #${g.bank_summary_id}`
                      : 'Not linked to bank statement — use Reconciliation to match'}
                  </p>
                </div>
                <div className="bp-outstanding">
                  <span className="bp-outstanding-label">Outstanding</span>
                  <strong>{formatMoney(g.flat_summary.total_outstanding)}</strong>
                </div>
              </div>
              <button type="button" className="bp-btn primary" onClick={() => openReview(g)}>
                Review &amp; approve
              </button>
            </article>
          ))}
        </div>
      )}

      {selected && (
        <div className="bp-modal-overlay" onClick={closeModal} role="presentation">
          <div
            className="bp-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="bp-modal-title"
          >
            <header className="bp-modal-header">
              <div>
                <h2 id="bp-modal-title">Approve payment — {flatDisplay(selected)}</h2>
                <p className="bp-muted">
                  {selected.bank_matched
                    ? `Linked to bank statement line #${selected.bank_summary_id}. You can approve after review.`
                    : 'Link this payment to a bank credit in Reconciliation before approving, or approve if already verified offline.'}
                </p>
                {!selected.bank_matched && (
                  <p className="bp-muted">
                    <Link to="/society/reconcile">Open Reconciliation</Link> to match UPI/bank ref{' '}
                    {selected.transaction_id ? `"${selected.transaction_id}"` : ''} with your statement.
                  </p>
                )}
              </div>
              <button type="button" className="bp-modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </header>

            <div className="bp-modal-body">
              <section className="bp-section">
                <div className="bp-received-card">
                  <h3 className="bp-received-card-header">Transaction Received</h3>
                  <div className="bp-verify-grid">
                  <div>
                    <span className="bp-field-label">Amount paid</span>
                    <strong className="bp-field-value bp-highlight">{formatMoney(selected.total_amount_paid)}</strong>
                  </div>
                  <div>
                    <span className="bp-field-label">Mode</span>
                    <strong className="bp-field-value">{selected.payment_mode || '—'}</strong>
                  </div>
                  <div>
                    <span className="bp-field-label">Transaction ref</span>
                    <strong className="bp-field-value">{selected.transaction_id || '—'}</strong>
                  </div>
                  <div>
                    <span className="bp-field-label">Submitted</span>
                    <strong className="bp-field-value">{formatBillingDateTime(selected.paid_at)}</strong>
                  </div>
                </div>
                {screenshotUrls[selected.group_key] && (
                  <a
                    href={screenshotUrls[selected.group_key]}
                    target="_blank"
                    rel="noreferrer"
                    className="bp-screenshot-link"
                  >
                    View payment screenshot
                  </a>
                )}
                </div>
              </section>

              <section className="bp-section">
                <h3>Flat outstanding (before approval)</h3>
                <p className="bp-summary-line">
                  Total due: <strong>{formatMoney(selected.flat_summary.total_outstanding)}</strong>
                  {' '}(principal {formatMoney(selected.flat_summary.total_principal)}
                  {' · '}interest {formatMoney(selected.flat_summary.total_interest)}
                  {selected.flat_summary.total_advance > 0
                    ? ` · advance credit ${formatMoney(selected.flat_summary.total_advance)}`
                    : ''})
                </p>
                <table className="bp-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Principal</th>
                      <th>Interest</th>
                      <th>Advance</th>
                      <th>Total due</th>
                      <th>Interest rule</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.categories.map((c) => (
                      <tr key={c.bill_type}>
                        <td>{c.category_display_name || c.bill_type}</td>
                        <td>{formatMoney(c.principal_outstanding)}</td>
                        <td className="bp-interest-col">{formatMoney(c.interest_outstanding)}</td>
                        <td>{formatMoney(c.advance_balance)}</td>
                        <td className="bp-strong">{formatMoney(c.total_due)}</td>
                        <td className="bp-muted-cell">{c.interest_configuration_label || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {(selected.pending_submission_count ?? 0) > 1 && (
                <section className="bp-section">
                  <h3>Pending payments for this flat</h3>
                  <p className="bp-muted">
                    {selected.pending_submission_count} separate submissions — approve together from this screen.
                  </p>
                  <table className="bp-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Submitted</th>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Ref</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selected.pending_submissions ?? []).map((sub, index) => (
                        <tr key={sub.payment_id}>
                          <td>{index + 1}</td>
                          <td>{formatBillingDateTime(sub.paid_at)}</td>
                          <td>{sub.category_display_name}</td>
                          <td className="bp-strong">{formatMoney(sub.amount)}</td>
                          <td className="bp-muted-cell">{sub.transaction_id || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              <section className="bp-section">
                <h3>Amount paid (allocation)</h3>
                <table className="bp-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Amount paid</th>
                      <th>To principal</th>
                      <th>To interest</th>
                      <th>To advance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.payment_by_category.map((slice) => (
                      <tr key={slice.payment_id}>
                        <td>{slice.category_display_name}</td>
                        <td className="bp-strong">{formatMoney(slice.amount_paid)}</td>
                        <td>{formatMoney(slice.principal_applied)}</td>
                        <td>{formatMoney(slice.interest_applied)}</td>
                        <td>{formatMoney(slice.advance_applied)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="bp-strong">Total</td>
                      <td className="bp-strong">{formatMoney(selected.total_amount_paid)}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </section>

              <section className="bp-section bp-waive-section">
                <h3>Waive interest (optional)</h3>
                <p className="bp-muted">
                  Waived amount is deducted from interest outstanding for that category when you approve.
                </p>
                {selected.payment_by_category.map((slice) => (
                  <div key={slice.payment_id} className="bp-waive-row">
                    <label>
                      {slice.category_display_name}
                      <span className="bp-muted"> (max {formatMoney(slice.interest_waivable_max)})</span>
                    </label>
                    <div className="bp-waive-inputs">
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        max={slice.interest_waivable_max}
                        value={waiveByCategory[slice.bill_type] ?? ''}
                        placeholder="0"
                        onChange={(e) =>
                          setWaiveByCategory((prev) => ({
                            ...prev,
                            [slice.bill_type]: e.target.value,
                          }))
                        }
                      />
                      <button
                        type="button"
                        className="bp-btn secondary bp-btn-sm"
                        onClick={() =>
                          setWaiveByCategory((prev) => ({
                            ...prev,
                            [slice.bill_type]: String(slice.interest_waivable_max),
                          }))
                        }
                      >
                        Waive full
                      </button>
                    </div>
                  </div>
                ))}
              </section>
            </div>

            <footer className="bp-modal-footer">
              <button
                type="button"
                className="bp-btn danger"
                disabled={busyKey === selected.group_key}
                onClick={() => handleReject(selected)}
              >
                <HiXCircle size={18} /> Reject
              </button>
              <button
                type="button"
                className="bp-btn success"
                disabled={busyKey === selected.group_key}
                onClick={() => handleApprove(selected)}
              >
                <HiCheckCircle size={18} /> Approve payment
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocietyAdminBillPayments;
